import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Incident, IncidentSeverity, IncidentStatus } from '@/types/incident';
import { useUsersStore } from '@nimbitlabs/features';
import { useIncidentsStore } from '@/stores/incidentsStore';
import { Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea, useToast } from '@nimbitlabs/ui';

const updateIncidentSchema = z.object({
  title: z.string().min(1, 'Incident title is required').max(200, 'Title must be 200 characters or less'),
  description: z.string().min(1, 'Description is required').max(2000, 'Description must be 2000 characters or less'),
  severity: z.nativeEnum(IncidentSeverity).optional(),
  status: z.nativeEnum(IncidentStatus).optional(),
  assignedToUserId: z.string().optional().nullable(),
});

type UpdateIncidentFormData = z.infer<typeof updateIncidentSchema>;

interface UpdateIncidentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incident: Incident | null;
  onIncidentUpdated: () => void;
  onIncidentDeleted: () => void;
}

export const UpdateIncidentModal: React.FC<UpdateIncidentModalProps> = ({
  open,
  onOpenChange,
  incident,
  onIncidentUpdated,
  onIncidentDeleted,
}) => {
  const { users } = useUsersStore();
  const { updateIncident: updateIncidentFn, deleteIncident: deleteIncidentFn } = useIncidentsStore();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const form = useForm<UpdateIncidentFormData>({
    resolver: zodResolver(updateIncidentSchema),
    defaultValues: {
      title: '',
      severity: undefined,
      status: undefined,
      description: '',
      assignedToUserId: undefined,
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open && incident) {
      // Convert severity and status from lowercase (API) to capitalized (enum)
      const capitalizedSeverity = incident.severity 
        ? (incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1).toLowerCase()) as IncidentSeverity
        : incident.severity;
      
      const capitalizedStatus = incident.status
        ? (incident.status.charAt(0).toUpperCase() + incident.status.slice(1).toLowerCase()) as IncidentStatus
        : incident.status;
      
      form.reset({
        title: incident.title,
        severity: capitalizedSeverity,
        status: capitalizedStatus,
        description: incident.description,
        assignedToUserId: incident.assignedToUserId,
      });
    }
  }, [open, incident, form]);

  const onSubmit = async (data: UpdateIncidentFormData) => {
    if (!incident) return;
    
    try {
      // Convert severity and status to lowercase for API if present
      // Handle 'unassigned' value for assignedToUserId
      const payload = {
        ...data,
        ...(data.severity && { severity: data.severity.toLowerCase() as any }),
        ...(data.status && { status: data.status.toLowerCase() as any }),
        assignedToUserId: data.assignedToUserId === 'unassigned' ? null : data.assignedToUserId,
      };
      await updateIncidentFn(incident.id, payload);
      toast({ title: 'Success', description: 'Incident updated successfully' });
      onIncidentUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update incident:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update incident',
        variant: 'destructive',
      });
    }
  };

  const handleResolve = async () => {
    if (!incident) return;

    try {
      await updateIncidentFn(incident.id, { 
          isResolved: true,
          status: IncidentStatus.Resolved 
        });
      toast({ title: 'Success', description: 'Incident resolved successfully' });
      onIncidentUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to resolve incident:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve incident',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!incident) return;
    
    try {
      await deleteIncidentFn(incident.id);
      toast({ title: 'Success', description: 'Incident deleted successfully' });
      onIncidentDeleted();
      onOpenChange(false);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete incident:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete incident',
        variant: 'destructive',
      });
    }
  };

  if (!incident) return null;

  const isResolved = !!incident.endTime;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update Incident</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Brief description of the incident" disabled={isResolved} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="severity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Severity *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={isResolved}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={IncidentSeverity.Low}>Low</SelectItem>
                          <SelectItem value={IncidentSeverity.Medium}>Medium</SelectItem>
                          <SelectItem value={IncidentSeverity.High}>High</SelectItem>
                          <SelectItem value={IncidentSeverity.Critical}>Critical</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={isResolved}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={IncidentStatus.Investigating}>Investigating</SelectItem>
                          <SelectItem value={IncidentStatus.Identified}>Identified</SelectItem>
                          <SelectItem value={IncidentStatus.Monitoring}>Monitoring</SelectItem>
                          <SelectItem value={IncidentStatus.Resolved}>Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Detailed description of the incident" rows={4} disabled={isResolved} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="assignedToUserId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign To</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || 'unassigned'} disabled={isResolved}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

            {incident.serviceName && (
              <div className="space-y-2">
                <Label>Service</Label>
                <div className="text-sm text-muted-foreground">{incident.serviceName}</div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Started At</Label>
              <div className="text-sm text-muted-foreground">
                {new Date(incident.startTime).toLocaleString()}
              </div>
            </div>

            {incident.endTime && (
              <div className="space-y-2">
                <Label>Resolved At</Label>
                <div className="text-sm text-muted-foreground">
                  {new Date(incident.endTime).toLocaleString()}
                </div>
              </div>
            )}

              {!isResolved && (
                <div className="pt-4 border-t space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResolve}
                    disabled={updateIncident.isPending}
                    className="w-full bg-green-500/10 hover:bg-green-500/20 text-green-600 border-green-500/20"
                  >
                    Mark as Resolved
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={updateIncident.isPending}
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Incident
                  </Button>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {isResolved ? 'Close' : 'Cancel'}
                </Button>
                {!isResolved && (
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? 'Updating...' : 'Update Incident'}
                  </Button>
                )}
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Incident</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this incident? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
